<template>
  <div ref="videoContainer" class="group w-full bg-black">
    <div class="relative flex w-full items-center justify-center">
      <VideoError v-if="hasError" />
      <template v-else>
        <mux-player
          :class="isLoading ? 'hidden' : ''"
          class="h-full w-full"
          ref="muxPlayer"
          :playback-id="playbackId"
          :playback-token="token"
          :thumbnail-token="thumbnailToken"
          loop
          :metadata-video-id="id"
          :metadata-video-title="title"
          :metadata-viewer-user-id="fanStore.fanId"
          metadata-sub-property-id="sparklejumpropequeen"
          :muted="isMuted"
          no-volume-pref="true"
          stream-type="on-demand"
          prefer-playback="mse"
          :debug="muxDebug"
          :env-key="muxEnvKey"
          playsinline
          loading="lazy"
          theme="microvideo"
          @error="handleError"
          @ended="handleVideoEnded"
          @playing="handlePlaying"
          @pause="handlePause"
          @loadstart="handleLoadStart"
          @loadeddata="handleLoadedData"
          @volumechange="handleVolumeChange"
        />
        <VideoLoader v-if="isLoading" class="min-h-[400px]" />
        <!-- <transition name="fade" mode="out-in">
          <VideoMuteButton v-if="showMuteButton" @toggle-mute="toggleMute" :is-muted="isMuted" />
        </transition> -->
        <!-- <VideoPlayButton v-if="!isPlaying && isPostPage" @click="play()" /> -->
        <!-- <VideoFullscreenOverlay @callback="toggleFullscreen" /> -->
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import '@mux/mux-player';
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
// import { nextTick } from 'vue';
import { useFanStore } from '@stores/fan.store';
import { useVideoPlayerStore } from '@/stores/videoPlayer.store';
import { useTelemetryFetcher } from '@api/tracking.api';
import { useI18n } from 'vue-i18n';
// import { useRoute } from 'vue-router';

import type { VideoPlayerInterface } from './videplayer.interface';

import VideoError from '@modules/VideoPlayer/VideoError.vue';
// import VideoFullscreenOverlay from '@modules/VideoPlayer/VideoFullscreenOverlay.vue';
// import VideoMuteButton from '@modules/VideoPlayer/VideoMuteButton.vue';
// import VideoPlayButton from '@modules/VideoPlayer/VideoPlayButton.vue';
import VideoLoader from './VideoLoader.vue';
import { storeToRefs } from 'pinia';

const muxDebug = false;
const muxEnvKey = import.meta.env.VITE_MUX_ENV_KEY;
interface MuxPlayerElement extends HTMLVideoElement {
  webkitEnterFullscreen?: () => Promise<void>;
  webkitExitFullscreen?: () => Promise<void>;
  media: HTMLVideoElement & {
    webkitEnterFullscreen?: () => Promise<void>;
    webkitExitFullscreen?: () => Promise<void>;
  };
  paused: boolean;
  muted: boolean;
  volume: number;
}

interface DocumentWithWebkit extends Document {
  webkitFullscreenElement: Element | null;
}

const props = defineProps<VideoPlayerInterface>();

// const route = useRoute();
const fanStore = useFanStore();
const videoPlayerStore = useVideoPlayerStore();
const { t } = useI18n();
const postTelemetry = useTelemetryFetcher();

const muxPlayer = ref<MuxPlayerElement | null>(null);
const videoError = ref<string | null>(null);
const contentViewCompleted = ref(false); // for telemetry
const contentViewStarted = ref(false); // for telemetry

// Computed
const isPlayerReady = ref(false);
const { isMuted } = storeToRefs(videoPlayerStore);
// const isPostPage = computed(() => route.name === 'FanzonePost');
const isPlaying = computed(
  () => !!muxPlayer.value && isPlayerReady.value && !muxPlayer.value.paused,
);
const hasError = computed(() => !!videoError.value);
const isLoading = computed(() => !hasError.value && !isPlaying.value && !isPlayerReady.value);
// const showMuteButton = computed(() => props.isActive && isPlayerReady.value);

// Video Player event handlers
const handleError = (event: Event) => {
  // console.log('handleError', event);
  const video = event.target as HTMLVideoElement;
  videoError.value = video?.error?.message || t('errors.couldNotLoadContent');
};
const handleVideoEnded = () => {
  console.log('handleVideoEnded');
  if (!muxPlayer.value) return;
  if (!contentViewCompleted.value) {
    contentViewCompleted.value = true;
    postTelemetry.execute({
      metric: 'view-post-video-complete',
      resource: `${window.location.href}?postId=${props.id}`,
    });
  }
  // Restart the video
  muxPlayer.value.currentTime = 0;
  muxPlayer.value.play();
};
// needed?
const handlePlaying = () => {
  // console.log('handlePlaying');
};
const handlePause = () => {
  // console.log('handlePause');
};
const handleLoadStart = () => {
  // console.log('%c handleLoadStart', 'color: hotpink; font-weight: bold;');
};
const handleLoadedData = () => {
  // console.log('%c handleLoadedData', 'color: orange; font-weight: bold;');
  isPlayerReady.value = true;
  if (props.isActive) play();
  recordViewTelemetry();
};
const handleVolumeChange = () => {
  if (!muxPlayer.value) return;
  isMuted.value = muxPlayer.value.muted;
};

// Video Player methods
const play = () => {
  // console.log('play()');
  if (muxPlayer.value) muxPlayer.value.play();
};
const pause = () => {
  // console.log('pause()');
  if (muxPlayer.value) muxPlayer.value.pause();
};
const unmute = () => {
  // console.log('unmute');
  if (!muxPlayer.value) return;
  isMuted.value = false;
  muxPlayer.value.muted = false;
  // muxPlayer.value.volume = 1;
};
const toggleFullscreen = async () => {
  const mux = muxPlayer.value;
  if (!mux) return;

  const muxShadowRoot = mux.shadowRoot;
  if (!muxShadowRoot) return;

  const mediaThemeSR = muxShadowRoot.querySelector('media-theme')?.shadowRoot;
  if (!mediaThemeSR) return;

  const fullscreenBtn = mediaThemeSR.querySelector('media-fullscreen-button') as HTMLButtonElement;

  if (fullscreenBtn) fullscreenBtn.click();
};

const recordViewTelemetry = () => {
  // console.log('telemetry');
  if (!contentViewStarted.value) {
    contentViewStarted.value = true;
    postTelemetry.execute({
      metric: 'view-post-video',
      resource: `${window.location.href}?postId=${props.id}`,
    });
  }
};

watch(
  () => props.isActive,
  (isActive) => {
    if (isActive) play();
    else pause();
  },
  { immediate: true },
);

const handleFullscreenChange = () => {
  // Check for both standard and webkit fullscreen
  const isFullscreen =
    document.fullscreenElement === muxPlayer.value ||
    (document as DocumentWithWebkit).webkitFullscreenElement === muxPlayer.value;

  if (isFullscreen) {
    // Add a small delay for iOS to ensure the fullscreen transition is complete
    setTimeout(() => {
      unmute();
      play();
    }, 100);
  }
};

const handleVideoClick = async (event: Event) => {
  event.stopPropagation();
  event.preventDefault();
  if (!muxPlayer.value) return;

  // Toggle fullscreen
  await toggleFullscreen();
};

onMounted(() => {
  document.addEventListener('fullscreenchange', handleFullscreenChange);
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

  // Add click handler to mux-video element
  if (muxPlayer.value?.shadowRoot) {
    const muxVideo = muxPlayer.value.shadowRoot.querySelector('mux-video');
    if (muxVideo) {
      muxVideo.addEventListener('click', handleVideoClick as EventListener);
    }
  }
});

onUnmounted(() => {
  document.removeEventListener('fullscreenchange', handleFullscreenChange);
  document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);

  // Remove click handler from mux-video element
  if (muxPlayer.value?.shadowRoot) {
    const muxVideo = muxPlayer.value.shadowRoot.querySelector('mux-video');
    if (muxVideo) {
      muxVideo.removeEventListener('click', handleVideoClick as EventListener);
    }
  }
});

defineExpose({
  toggleFullscreen,
});
</script>
